import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { AddButton } from "@fp/ui/components/base/buttons/add-button";
import { Button } from "@fp/ui/components/base/buttons/button";
import { Dialog, Modal, ModalOverlay } from "@fp/ui/components/application/modals/modal";
import { Heading } from "react-aria-components";
import { File06, X } from "@untitledui/icons";
import { useSetPageActions } from "@/components/page-actions-context";
import { PageActionsMenu } from "@/components/page-actions-menu";
import {
	PageContentContainer,
	PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { OpportunityList } from "@fp/ui/components/opportunities/opportunity-list";
import { SaveTemplateHint } from "@fp/ui/components/opportunities/save-template-hint";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import axios from "@/lib/axios";
import type { OpportunityStatsDto } from "@fp/admin-api-client";

export const Route = createFileRoute("/opps/")({
	component: RouteComponent,
	loader: async ({ context: { queryClient } }) => {
		const oppStats = await queryClient.fetchQuery({
			queryKey: ["opportunities", "stats"],
			queryFn: () => axios.get<OpportunityStatsDto>("/api/opportunities/stats"),
		});

		if (oppStats.data?.all === 0) {
			redirect({ to: "/opps/new" });
		}

		return oppStats?.data ?? ({} as OpportunityStatsDto);
	},
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const oppStats = Route.useLoaderData();
	const navigate = useNavigate();
	const [showNewOppModal, setShowNewOppModal] = useState(false);

	useSetPageActions("Opps", useMemo(() => [
		{ id: "manage-templates", icon: File06, label: "Manage Templates", onAction: () => navigate({ to: "/opps/templates" }) },
	], [navigate]));

	const onItemSelect = (category: string) => {
		navigate({
			to: `/opps/categories/$category`,
			params: { category },
		});
	};

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Opps"
				titleActions={
					<>
						<AddButton onClick={() => setShowNewOppModal(true)}>New</AddButton>
						<PageActionsMenu />
					</>
				}
			/>
			<PageContentContainer className="p-0">
				<section className="flex flex-col bg-white flex-grow">
					<OpportunityList stats={oppStats} onItemSelect={onItemSelect} />
				</section>
			</PageContentContainer>
			<ModalOverlay isOpen={showNewOppModal} onOpenChange={(open) => !open && setShowNewOppModal(false)} className="items-center">
				<Modal className="max-w-md">
					<Dialog>
						<div className="bg-white rounded-lg w-full">
							<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
								<Heading slot="title" level={3} className="text-lg font-semibold text-gray-900">
									Create an Opp
								</Heading>
								<Button color="tertiary" size="sm" onClick={() => setShowNewOppModal(false)}>
									<X className="size-5" />
								</Button>
							</div>
							<div className="flex gap-3 p-4">
								<Button
									color="secondary"
									className="flex-1"
									onClick={() => {
										setShowNewOppModal(false);
										navigate({ to: "/opps/new" });
									}}
								>
									Start from Scratch
								</Button>
								<Button
									color="primary"
									className="flex-1"
									onClick={() => {
										setShowNewOppModal(false);
										navigate({ to: "/opps/templates" });
									}}
								>
									Use a Template
								</Button>
							</div>
							<div className="px-4 pb-4">
								<SaveTemplateHint />
							</div>
						</div>
					</Dialog>
				</Modal>
			</ModalOverlay>
		</AdminLayout>
	);
}
